import { formatCurrency, calculatePortfolioTotalsByMode, type AllocationData, type CalculationMode } from "@/lib/portfolioCalculations";
import { useState, useEffect } from "react";
import { OverflowDialog } from "./OverflowDialog";

interface TotalsSectionProps {
  portfolioSize?: number;
  allocations?: AllocationData[];
  onAutoSize?: (targetSize: number) => void;
  isPortfolioSizeLocked?: boolean;
  mode?: CalculationMode; // 'percentage' for model, 'dollar' for client/personal; defaults to 'auto'
  // Optimistic overrides: allocationId -> percentage
  overrides?: Record<string, number>;
  // Display/base currency for formatting (display-only)
  currency?: string;
}

export const TotalsSection = ({ 
  portfolioSize = 0, 
  allocations = [],
  onAutoSize,
  isPortfolioSizeLocked = false,
  mode = 'auto',
  overrides = {},
  currency
}: TotalsSectionProps) => {
  const [isOverflowDialogOpen, setIsOverflowDialogOpen] = useState(false);
  const isModel = mode === 'percentage';
  
  // Apply optimistic overrides for a separate optimistic calculation
  // For display in dollar mode, we will still use persisted totals below
  let effectiveAllocations = (allocations || []).map((a) => {
    const id = a.id as string | undefined;
    if (!id) return a;
    const ov = overrides[id];
    if (ov === undefined) return a;
    // Percentage (model) portfolios: reflect override immediately for smooth UX
    if (mode === 'percentage') {
      return { ...a, allocation_percentage: ov };
    }
    // Dollar (personal/client) portfolios: for optimistic overflow detection, synthesize dollars
    const val = Math.round(((Number(ov) || 0) / 100 * portfolioSize) * 100) / 100;
    return { ...a, allocation_percentage: Number(ov) || 0, allocation_value: val, is_value_locked: true } as any;
  });

  // Fallback: if allocations temporarily empty during refetch, synthesize from overrides
  if ((effectiveAllocations?.length || 0) === 0 && overrides && Object.keys(overrides).length > 0) {
    // Only synthesize from overrides for percentage mode; in dollar mode we rely on persisted DB values
    if (mode === 'percentage') {
      effectiveAllocations = Object.entries(overrides).map(([id, pct]) => {
        const safePct = Number(pct) || 0;
        return { id, allocation_percentage: safePct } as any;
      });
    }
  }

  // Calculate optimistic totals (with overrides synthesis above)
  const optimisticTotals = calculatePortfolioTotalsByMode(effectiveAllocations, portfolioSize, mode);

  // Calculate persisted totals (ignore overrides in dollar mode)
  const persistedTotals = calculatePortfolioTotalsByMode(
    (allocations || []).map((a) => ({ ...a })),
    portfolioSize,
    mode
  );
  
  // For overflow detection in dollar mode, consider either optimistic OR persisted overflow
  const hasOverflow = isModel ? optimisticTotals.hasOverflow : (optimisticTotals.hasOverflow || persistedTotals.hasOverflow);
  const overflowRequiredSize = isModel
    ? optimisticTotals.totalDollarAmount
    : (optimisticTotals.hasOverflow ? optimisticTotals.totalDollarAmount : persistedTotals.totalDollarAmount);

  // For display values, keep persisted values in dollar mode to avoid double counting, optimistic in model
  const displayTotals = isModel ? optimisticTotals : persistedTotals;

  const valueColorClass = displayTotals.isFullyAllocated ? "text-green-600" : "text-destructive";

  // Check if user has seen overflow dialog this session
  const hasSeenOverflowDialog = () => {
    return sessionStorage.getItem('portfolio-overflow-seen') === 'true';
  };

  // Get user's overflow preference from session
  const getOverflowPreference = () => {
    return sessionStorage.getItem('portfolio-overflow-preference');
  };

  // Handle overflow detection and automatic actions (only for dollar mode)
  useEffect(() => {
    console.debug('[TotalsSection] Overflow check:', {
      hasOverflow,
      isPortfolioSizeLocked,
      hasOnAutoSize: !!onAutoSize,
      optimisticTotal: optimisticTotals.totalDollarAmount,
      persistedTotal: persistedTotals.totalDollarAmount,
      portfolioSize
    });

    if (isModel) return; // No auto-size for model portfolios
    if (!hasOverflow || isPortfolioSizeLocked || !onAutoSize) return;

    // Suppress dialog if a shrink attempt was just blocked to avoid reversed UX
    try {
      const suppressUntil = Number(sessionStorage.getItem('portfolio-overflow-suppress') || '0');
      if (suppressUntil && Date.now() < suppressUntil) {
        console.debug('[TotalsSection] Suppressing overflow dialog due to recent blocked shrink');
        return;
      }
    } catch {}

    // Suppress dialog/auto-size while a programmatic auto-sizing is in progress
    try {
      const autosizingUntil = Number(sessionStorage.getItem('portfolio-overflow-autosizing') || '0');
      if (autosizingUntil && Date.now() < autosizingUntil) {
        console.debug('[TotalsSection] Autosizing in progress - skipping dialog/auto-size');
        return;
      }
    } catch {}

    // If autosizing is ongoing or a pending oversize exists, rely on persisted totals for the required size to avoid doubling
    let effectiveRequiredSize = overflowRequiredSize;
    try {
      const autosizingUntil = Number(sessionStorage.getItem('portfolio-overflow-autosizing') || '0');
      const pendingRaw = sessionStorage.getItem('portfolio-pending-oversize');
      const pending = pendingRaw ? JSON.parse(pendingRaw) : null;
      const autosizingActive = !!(autosizingUntil && Date.now() < autosizingUntil);
      const pendingActive = !!(pending && pending.expires > Date.now());
      if (autosizingActive || pendingActive) {
        effectiveRequiredSize = persistedTotals.totalDollarAmount;
        console.debug('[TotalsSection] Using persisted totals during autosize/pending for required size', {
          effectiveRequiredSize,
          persisted: persistedTotals.totalDollarAmount,
          optimistic: optimisticTotals.totalDollarAmount,
          autosizingActive,
          pendingActive
        });
      }
    } catch {}

    // If a force flag is present (from blocked oversize edit), immediately show the dialog
    try {
      const forceUntil = Number(sessionStorage.getItem('portfolio-overflow-force') || '0');
      if (forceUntil && Date.now() < forceUntil) {
        console.debug('[TotalsSection] Force-opening overflow dialog');
        setIsOverflowDialogOpen(true);
        sessionStorage.setItem('portfolio-overflow-seen', 'true');
        sessionStorage.removeItem('portfolio-overflow-force');
        return;
      }
    } catch {}

    // If computed required size equals current size (within epsilon), do nothing
    if (Math.abs((effectiveRequiredSize || 0) - (portfolioSize || 0)) < 0.01) {
      console.debug('[TotalsSection] Skipping overflow handling: required size equals current size', {
        overflowRequiredSize: effectiveRequiredSize,
        portfolioSize,
      });
      return;
    }

    const preference = getOverflowPreference();
    if (!hasSeenOverflowDialog()) {
      console.debug('[TotalsSection] Showing overflow dialog for first time');
      setIsOverflowDialogOpen(true);
      sessionStorage.setItem('portfolio-overflow-seen', 'true');
    } else if (preference === 'auto-size') {
      console.debug('[TotalsSection] Auto-sizing based on user preference');
      onAutoSize?.(effectiveRequiredSize);
    }
    // If preference is 'keep-current', do nothing
  }, [hasOverflow, isPortfolioSizeLocked, onAutoSize, optimisticTotals.totalDollarAmount, persistedTotals.totalDollarAmount, portfolioSize]);

  const handleAutoSize = (targetSize: number) => {
    onAutoSize?.(targetSize);
  };

  const handleKeepCurrent = () => {
    // No action needed - just close dialog
  };

  return (
    <>
      <div className="mt-8 p-4 border rounded-lg bg-muted/50">
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium">Total:</span>
          <div className="flex gap-6 items-center">
            <span className={`text-sm font-medium ${valueColorClass}`}>
              {formatCurrency(displayTotals.totalDollarAmount, currency)}
            </span>
            <span className={`text-sm font-medium ${valueColorClass}`}>
              {displayTotals.totalPercentage.toFixed(2)}%
            </span>
          </div>
        </div>
        {displayTotals.isFullyAllocated ? (
          <div className="mt-2 text-green-600 text-right text-sm font-medium">
            Your Portfolio is 100% allocated.
          </div>
        ) : (
          <div className="mt-2 text-blue-600 text-right text-sm font-medium">
            Please allocate 100% of your portfolio for analysis.
          </div>
        )}
        {isModel ? (
          // Model portfolios: treat overflow purely as percentage > 100
          displayTotals.totalPercentage > 100.01 && (
            <div className="mt-2 text-orange-600 text-right text-sm font-medium">
              {`Percentages exceed 100% by ${(displayTotals.totalPercentage - 100).toFixed(2)}%. Reduce allocations to 100%.`}
            </div>
          )
        ) : (
          hasOverflow && (
            <div className="mt-2 text-orange-600 text-right text-sm font-medium">
              {isPortfolioSizeLocked 
                ? `Allocations exceed locked portfolio size by ${formatCurrency((optimisticTotals.hasOverflow ? optimisticTotals : persistedTotals).overflowAmount, currency)}. Unlock to auto-size.`
                : `Allocations exceed portfolio size by ${formatCurrency((optimisticTotals.hasOverflow ? optimisticTotals : persistedTotals).overflowAmount, currency)}.`
              }
            </div>
          )
        )}
      </div>

      <OverflowDialog
        isOpen={isOverflowDialogOpen}
        onOpenChange={setIsOverflowDialogOpen}
        currentSize={portfolioSize}
        requiredSize={overflowRequiredSize}
        onAutoSize={handleAutoSize}
        onKeepCurrent={handleKeepCurrent}
      />
    </>
  );
};